import { type SettingDetailDto, type SettingValueSource, isSameScope } from '@aysar/contracts';

import { type DirectPriceMap, priceMapOf } from '../direct-price-map/direct-price-map.util';
import {
  SETTING_SCOPE_IDS,
  type SettingScopeId,
  scopeOfId,
} from '../setting-scopes/setting-scopes.util';

export { scopeOfId };

/**
 * The columns the pricing board draws: one per scope `billing.directPrices`
 * allows (`scopesAllowed: ['global', 'gender']`) — which is every scope a
 * gender-scoped setting has, so the list itself lives in `setting-scopes`.
 */
export const DIRECT_PRICE_SCOPE_IDS = SETTING_SCOPE_IDS;

export type DirectPriceScopeId = SettingScopeId;

/**
 * The map **stored** at one scope — the override, not what resolves.
 *
 * This is what a write is composed from. Composing from the effective value
 * instead would freeze today's inherited numbers into that scope's own row, and
 * a later change to the global price would then silently stop reaching it —
 * `useAiPricing`'s rule, and it bites harder here because the inheritance is
 * per-gender rather than per-field.
 */
export function storedPricesOf(
  detail: SettingDetailDto | undefined,
  id: DirectPriceScopeId,
): DirectPriceMap {
  const scope = scopeOfId(id);

  return priceMapOf(detail?.values.find((row) => isSameScope(row, scope))?.value);
}

export interface EffectivePrice {
  /** `null` is unpriced — not zero, which is a plan given away deliberately. */
  amount: number | null;
  /** Which layer answered: this scope's own row, global, or the coded default. */
  source: SettingValueSource;
}

/**
 * What one product costs at one scope right now, and which layer decided it.
 *
 * The source is the whole point of the board: 199 for men inherited from global
 * and 199 for men stored against men are the same number and different facts,
 * and only one of them survives a change to the global price.
 */
export function effectivePriceOf(
  detail: SettingDetailDto | undefined,
  id: DirectPriceScopeId,
  productKey: string,
): EffectivePrice {
  const scope = scopeOfId(id);
  const row = detail?.effective.find((candidate) => isSameScope(candidate, scope));

  if (row === undefined) {
    // No row for an allowed scope means the api resolved nothing for it, which
    // is the coded default — reported as such rather than as a missing scope,
    // because the board must still draw a cell.
    return { amount: null, source: 'default' };
  }

  return { amount: priceMapOf(row.value)[productKey] ?? null, source: row.source };
}
